/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 23/12/21
 */

import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import {
    useStatefulRequest,
    UseStatefulRequestOptions,
    UseStatefulRequestOutput,
} from '@/utils/useStatefulRequests/useStatefulRequest';

export type UseStatefulRequestWithDataOutput<ResponseData, RequestData = never> = Omit<
    UseStatefulRequestOutput<ResponseData, RequestData>,
    'makeRequest'
> & {
    makeRequest: (
        url: string,
        requestData: RequestData,
        config?: AxiosRequestConfig<RequestData>,
    ) => Promise<AxiosResponse<ResponseData, RequestData>>;
};

export const useStatefulRequestWithData = <ResponseData, RequestData = never>(
    method: Method,
    options?: UseStatefulRequestOptions,
): UseStatefulRequestWithDataOutput<ResponseData, RequestData> => {
    const {
        makeRequest: makeBaseRequest,
        responseData,
        loading,
        error,
        cancel,
        clear,
    } = useStatefulRequest<ResponseData, RequestData>(options);

    const makeRequestWithData = async (
        url: string,
        requestData: RequestData,
        config?: AxiosRequestConfig<RequestData>,
    ) => {
        return await makeBaseRequest({
            ...config,
            method,
            url,
            data: requestData,
        });
    };

    return {
        makeRequest: makeRequestWithData,
        responseData,
        loading,
        error,
        cancel,
        clear,
    };
};
